import axios from 'axios'

const API_URL = process.env.VUE_APP_API_URL || '/api'
//const API_URL = process.env.VUE_APP_API_URL || 'http://127.0.0.1:8079/'


// Create an axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 30000  // 30 sec
})

// Add a request interceptor to add auth token to all requests
apiClient.interceptors.request.use(
  config => {
    const sessionStr = localStorage.getItem('session')
    const session = sessionStr ? JSON.parse(sessionStr) : null
    
    if (session && session.token) {
      config.headers['Authorization'] = `Bearer ${session.token}`
    }
    
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Add a response interceptor to handle auth errors
apiClient.interceptors.response.use(
  response => response,
  error => {
    // If unauthorized, redirect to login
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('session')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export default {
  // Authentication APIs
  requestLoginToken(email) {
    console.log(`[DEBUG] Requesting login token for: ${email}`)
    return apiClient.post('/auth/request-token', { email })
      .then(response => {
        console.log('[DEBUG] Login token request successful:', response.data)
        return response
      })
      .catch(error => {
        console.error('[DEBUG] Login token request error:', error);
        // Enhanced error debugging
        if (error.response) {
          console.error('[DEBUG] Error response data:', error.response.data);
          console.error('[DEBUG] Error response status:', error.response.status);
          if (error.response.data && error.response.data.message) {
            throw new Error(error.response.data.message);
          }
        } else if (error.request) {
          console.error('[DEBUG] Error request (no response received):', error.request);
          throw new Error('Server not responding. Please try again later.');
        } else {
          console.error('[DEBUG] Error setting up request:', error.message);
          throw new Error('Network error. Please check your connection and try again.');
        }
        throw error;
      });
  },
  
  verifyLoginToken(email, token, sessionId) {
    console.log(`[DEBUG] Verifying token for: ${email}, sessionId: ${sessionId}`)
    return apiClient.post('/auth/verify-token', { 
      email, 
      token,
      sessionId
    })
    .then(response => {
      console.log('[DEBUG] Token verification successful:', response.data)
      return response
    })
    .catch(error => {
      console.error('[DEBUG] Token verification error:', error);
      // Enhanced error debugging
      if (error.response) {
        console.error('[DEBUG] Error response data:', error.response.data);
        console.error('[DEBUG] Error response status:', error.response.status);
        if (error.response.data && error.response.data.message) {
          throw new Error(error.response.data.message);
        }
      } else if (error.request) {
        console.error('[DEBUG] Error request (no response received):', error.request);
        throw new Error('Server not responding. Please try again later.');
      } else {
        console.error('[DEBUG] Error setting up request:', error.message);
        throw new Error('Network error. Please check your connection and try again.');
      }
      throw error;
    });
  },
  
  logout() {
    const sessionStr = localStorage.getItem('session')
    const session = sessionStr ? JSON.parse(sessionStr) : null
    
    if (session) {
      return apiClient.post('/auth/logout', { 
        userId: session.userId, 
        token: session.token 
      })
    }
    
    // If no session, just resolve
    return Promise.resolve()
  },
  
  // Chat API
  sendMessage(messages, context = null, userId = null) {
    return apiClient.post('/chat', {
      messages: messages,
      context: context,
      userId: userId
    })
  },

  // Upload APIs
  uploadSnapshot(file) {
    const formData = new FormData()
    formData.append('image', file)
    return apiClient.post('/upload/snapshot', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  uploadJsonData(data) {
    return apiClient.post('/upload/json', {
      data: data
    })
  },

  uploadDataFile(file) {
    const formData = new FormData()
    formData.append('datafile', file)
    return apiClient.post('/upload/datafile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  // Feedback API
  sendFeedback(userId, messageId, thumb, comment = '') {
    return apiClient.post('/feedback', {
      userId: userId,
      messageId: messageId,
      thumb: thumb,
      comment: comment
    })
  },

  // Health check
  checkHealth() {
    return apiClient.get('/health')
  }
}
